import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, Image, Video, Calendar, Users, Settings, LogOut, 
  MoreVertical, Eye, EyeOff, Trash2, Edit, Loader2,
  FolderPlus, ChevronRight, ImagePlus
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedGallery, setSelectedGallery] = useState(null);

  useEffect(() => {
    fetchGalleries();
  }, []);

  const fetchGalleries = async () => {
    try {
      const response = await axios.get(`${API}/admin/galleries`);
      setGalleries(response.data);
    } catch (error) {
      console.error('Error fetching galleries:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-white border-r border-gray-200 z-40">
        {/* Logo */}
        <div className="p-6 border-b border-gray-100">
          <a href="/" className="flex items-center gap-2">
            <span className="font-wedding text-2xl text-gray-800">Admin</span>
          </a>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1">
          <a 
            href="/admin" 
            className="flex items-center gap-3 px-4 py-3 text-sm font-medium bg-gray-100 rounded-lg text-black"
          >
            <Image className="w-5 h-5" />
            Gallerie
          </a>
          <a 
            href="/admin/settings" 
            className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
          >
            <Settings className="w-5 h-5" />
            Impostazioni
          </a>
        </nav>

        {/* User section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-100">
          <div className="flex items-center gap-3 px-4 py-3">
            <div className="w-10 h-10 bg-[#2a2a1c] rounded-full flex items-center justify-center text-white font-medium">
              A
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">Admin</p>
              <p className="text-xs text-gray-500">Fotografo</p>
            </div>
            <button 
              onClick={handleLogout}
              className="p-2 text-gray-400 hover:text-red-500 transition-colors"
              title="Logout"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-serif">Le tue Gallerie</h1>
            <p className="text-gray-500 text-sm mt-1">
              {galleries.length} {galleries.length === 1 ? 'galleria' : 'gallerie'} totali
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-5 py-2.5 bg-black text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Nuova Galleria
          </button>
        </div>

        {/* Galleries Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : galleries.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Image className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium mb-2">Nessuna galleria</h3>
            <p className="text-gray-500 mb-6">Crea la tua prima galleria per iniziare</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-black text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Nuova Galleria
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {galleries.map((gallery) => (
              <GalleryCard 
                key={gallery.id} 
                gallery={gallery} 
                onRefresh={fetchGalleries}
                onClick={() => navigate(`/admin/gallery/${gallery.id}`)}
                onEdit={() => {
                  setSelectedGallery(gallery);
                  setShowEditModal(true);
                }}
              />
            ))}
          </div>
        )}
      </main>

      {/* Create Modal */}
      {showCreateModal && (
        <CreateGalleryModal 
          onClose={() => setShowCreateModal(false)} 
          onCreated={() => {
            setShowCreateModal(false);
            fetchGalleries();
          }}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && selectedGallery && (
        <EditGalleryModal 
          gallery={selectedGallery}
          onClose={() => {
            setShowEditModal(false);
            setSelectedGallery(null);
          }} 
          onUpdated={() => {
            setShowEditModal(false);
            setSelectedGallery(null);
            fetchGalleries();
          }}
        />
      )}
    </div>
  );
};

// Gallery Card Component
const GalleryCard = ({ gallery, onRefresh, onClick, onEdit }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [coverProgress, setCoverProgress] = useState(0);
  const coverInputRef = React.useRef(null);

  const handleCoverSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    
    setUploadingCover(true);
    setCoverProgress(0);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/admin/galleries/${gallery.id}/cover/upload`, formData, {
        onUploadProgress: (ev) => {
          if (ev.total) setCoverProgress(Math.round((ev.loaded * 100) / ev.total));
        }
      });
      onRefresh();
    } catch (error) {
      console.error('Error uploading cover:', error);
      alert(error.response?.data?.detail || 'Errore durante il caricamento della copertina');
    } finally {
      setUploadingCover(false);
    }
  };

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Sei sicuro di voler eliminare "${gallery.title}"? Questa azione è irreversibile.`)) {
      return;
    }
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/galleries/${gallery.id}`);
      onRefresh();
    } catch (error) {
      console.error('Error deleting gallery:', error);
      alert('Errore durante l\'eliminazione');
    } finally {
      setDeleting(false);
    }
  };

  const toggleActive = async (e) => {
    e.stopPropagation();
    try {
      await axios.put(`${API}/admin/galleries/${gallery.id}`, {
        is_active: !gallery.is_active
      });
      onRefresh();
    } catch (error) {
      console.error('Error updating gallery:', error);
    }
    setShowMenu(false);
  };

  return (
    <div 
      className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
      onClick={onClick}
    >
      {/* Cover Image */}
      <div className="aspect-[4/3] bg-gray-100 relative">
        <input
          ref={coverInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onClick={(e) => e.stopPropagation()}
          onChange={handleCoverSelect}
          data-testid="gallery-cover-file-input"
        />
        {uploadingCover && (
          <div className="absolute inset-0 bg-black/40 z-30 flex flex-col items-center justify-center gap-3 px-8">
            <Loader2 className="w-8 h-8 text-white animate-spin" />
            <span className="text-white text-xs font-medium">
              {coverProgress < 100 ? `Caricamento copertina... ${coverProgress}%` : 'Elaborazione...'}
            </span>
            <div className="w-full max-w-[200px] bg-white/30 rounded-full h-1.5 overflow-hidden">
              <div 
                className="h-full bg-white transition-all duration-200"
                style={{ width: `${coverProgress}%` }}
              />
            </div>
          </div>
        )}
        {gallery.cover_url ? (
          <img 
            src={gallery.cover_url} 
            alt={gallery.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Image className="w-12 h-12 text-gray-300" />
          </div>
        )}
        
        {/* Status Badge */}
        <div className={`absolute top-3 left-3 px-2 py-1 rounded-full text-xs font-medium ${
          gallery.is_active 
            ? 'bg-green-100 text-green-700' 
            : 'bg-gray-100 text-gray-600'
        }`}>
          {gallery.is_active ? 'Attiva' : 'Nascosta'}
        </div>

        {/* Menu Button */}
        <div className="absolute top-3 right-3 z-20">
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setShowMenu(!showMenu);
            }}
            className="p-2 bg-white/90 rounded-full hover:bg-white transition-colors"
          >
            <MoreVertical className="w-4 h-4" />
          </button>
          
          {showMenu && (
            <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-30">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMenu(false);
                  onEdit();
                }}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                data-testid="gallery-menu-edit-btn"
              >
                <Edit className="w-4 h-4" /> Modifica
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMenu(false);
                  coverInputRef.current?.click();
                }}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                data-testid="gallery-menu-upload-cover-btn"
              >
                <ImagePlus className="w-4 h-4" /> Carica copertina
              </button>
              <button
                onClick={toggleActive}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
              >
                {gallery.is_active ? (
                  <><EyeOff className="w-4 h-4" /> Nascondi</>  
                ) : (
                  <><Eye className="w-4 h-4" /> Rendi visibile</>
                )}
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-red-600"
              >
                <Trash2 className="w-4 h-4" /> Elimina
              </button>
            </div>
          )}
        </div>

        {/* Hover overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
          <div className="opacity-0 group-hover:opacity-100 transition-opacity">
            <ChevronRight className="w-10 h-10 text-white drop-shadow-lg" />
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="p-4">
        <h3 className="font-medium text-lg mb-1">{gallery.title}</h3>
        <div className="flex items-center gap-4 text-sm text-gray-500">
          <span className="flex items-center gap-1">
            <Image className="w-4 h-4" />
            {gallery.photo_count}
          </span>
          <span className="flex items-center gap-1">
            <Video className="w-4 h-4" />
            {gallery.video_count}
          </span>
          {gallery.event_date && (
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {new Date(gallery.event_date).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// Create Gallery Modal
const CreateGalleryModal = ({ onClose, onCreated }) => {
  const [formData, setFormData] = useState({
    title: '',
    event_date: '',
    description: '',
    sposi_username: '',
    sposi_password: '',
    amici_username: '',
    amici_password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validation
    if (!formData.title || !formData.sposi_username || !formData.sposi_password || 
        !formData.amici_username || !formData.amici_password) {
      setError('Compila tutti i campi obbligatori');
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${API}/admin/galleries`, {
        ...formData,
        event_date: formData.event_date ? new Date(formData.event_date).toISOString() : null
      });
      onCreated();
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore durante la creazione');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-xl font-serif">Nuova Galleria</h2>
          <p className="text-sm text-gray-500 mt-1">Crea una galleria per un nuovo matrimonio</p>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}
          
          {/* Title */}
          <div>
            <label className="block text-sm font-medium mb-2">Titolo *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10"
              placeholder="es. Marco & Giulia"
            />
          </div>
          
          {/* Event Date */}
          <div>
            <label className="block text-sm font-medium mb-2">Data Evento</label>
            <input
              type="date"
              value={formData.event_date}
              onChange={(e) => setFormData({...formData, event_date: e.target.value})}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10"
            />
          </div>
          
          {/* Description */}
          <div>
            <label className="block text-sm font-medium mb-2">Descrizione</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 resize-none"
              rows={3}
              placeholder="Note o descrizione dell'evento"
            />
          </div>
          
          {/* Sposi Credentials */}
          <div className="p-4 bg-gray-50 rounded-lg space-y-4">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Users className="w-4 h-4" />
              Accesso Sposi
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Username *</label>
                <input
                  type="text"
                  value={formData.sposi_username}
                  onChange={(e) => setFormData({...formData, sposi_username: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
                  placeholder="marco.giulia"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Password *</label>
                <input
                  type="text"
                  value={formData.sposi_password}
                  onChange={(e) => setFormData({...formData, sposi_password: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
                  placeholder="password123"
                />
              </div>
            </div>
          </div>
          
          {/* Amici Credentials */}
          <div className="p-4 bg-gray-50 rounded-lg space-y-4">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Users className="w-4 h-4" />
              Accesso Amici
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Username *</label>
                <input
                  type="text"
                  value={formData.amici_username}
                  onChange={(e) => setFormData({...formData, amici_username: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
                  placeholder="amici.marco.giulia"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Password *</label>
                <input
                  type="text"
                  value={formData.amici_password}
                  onChange={(e) => setFormData({...formData, amici_password: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
                  placeholder="amici123"
                />
              </div>
            </div>
          </div>
          
          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-gray-300 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-3 bg-black text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Creazione...</>
              ) : (
                'Crea Galleria'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Gallery Modal
const EditGalleryModal = ({ gallery, onClose, onUpdated }) => {
  const [formData, setFormData] = useState({
    title: gallery.title || '',
    event_date: gallery.event_date ? gallery.event_date.split('T')[0] : '',
    description: gallery.description || '',
    sposi_username: '',
    sposi_password: '',
    amici_username: '',
    amici_password: ''
  });
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState('');

  // Load full gallery data including credentials
  useEffect(() => {
    const fetchGalleryDetails = async () => {
      try {
        const res = await axios.get(`${API}/admin/galleries/${gallery.id}`);
        setFormData({
          title: res.data.title || '',
          event_date: res.data.event_date ? res.data.event_date.split('T')[0] : '',
          description: res.data.description || '',
          sposi_username: res.data.sposi_username || '',
          sposi_password: res.data.sposi_password || '',
          amici_username: res.data.amici_username || '',
          amici_password: res.data.amici_password || ''
        });
      } catch (err) {
        console.error('Error fetching gallery details:', err);
      } finally {
        setLoadingData(false);
      }
    };
    
    fetchGalleryDetails();
  }, [gallery.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.title) {
      setError('Il titolo è obbligatorio');
      return;
    }
    
    setLoading(true);
    
    try {
      const updateData = {
        title: formData.title,
        event_date: formData.event_date ? new Date(formData.event_date).toISOString() : null,
        description: formData.description,
        sposi_username: formData.sposi_username,
        amici_username: formData.amici_username
      };
      
      // Only include passwords if they were changed
      if (formData.sposi_password) {
        updateData.sposi_password = formData.sposi_password;
      }
      if (formData.amici_password) {
        updateData.amici_password = formData.amici_password;
      }
      
      await axios.put(`${API}/admin/galleries/${gallery.id}`, updateData);
      onUpdated();
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore durante l\'aggiornamento');
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl p-8 flex items-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Caricamento...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-xl font-serif">Modifica Galleria</h2>
          <p className="text-sm text-gray-500 mt-1">Modifica le proprietà della galleria</p>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}
          
          {/* Title */}
          <div>
            <label className="block text-sm font-medium mb-2">Titolo *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10"
              placeholder="es. Marco & Giulia"
            />
          </div>
          
          {/* Event Date */}
          <div>
            <label className="block text-sm font-medium mb-2">Data Evento</label>
            <input
              type="date"
              value={formData.event_date}
              onChange={(e) => setFormData({...formData, event_date: e.target.value})}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10"
            />
          </div>
          
          {/* Description */}
          <div>
            <label className="block text-sm font-medium mb-2">Descrizione</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 resize-none"
              rows={3}
              placeholder="Note o descrizione dell'evento"
            />
          </div>
          
          {/* Sposi Credentials */}
          <div className="p-4 bg-gray-50 rounded-lg space-y-4">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Users className="w-4 h-4" />
              Accesso Sposi
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Username</label>
                <input
                  type="text"
                  value={formData.sposi_username}
                  onChange={(e) => setFormData({...formData, sposi_username: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Nuova Password (opzionale)</label>
                <input
                  type="text"
                  value={formData.sposi_password}
                  onChange={(e) => setFormData({...formData, sposi_password: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
                  placeholder="Lascia vuoto per non cambiare"
                />
              </div>
            </div>
          </div>
          
          {/* Amici Credentials */}
          <div className="p-4 bg-gray-50 rounded-lg space-y-4">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Users className="w-4 h-4" />
              Accesso Amici
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Username</label>
                <input
                  type="text"
                  value={formData.amici_username}
                  onChange={(e) => setFormData({...formData, amici_username: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Nuova Password (opzionale)</label>
                <input
                  type="text"
                  value={formData.amici_password}
                  onChange={(e) => setFormData({...formData, amici_password: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
                  placeholder="Lascia vuoto per non cambiare"
                />
              </div>
            </div>
          </div>
          
          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-gray-300 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-3 bg-black text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Salvataggio...</>
              ) : (
                'Salva Modifiche'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminDashboard;
